import { PaymentStatus } from '@/api/ariadne/__generated_types__/apollo';
import {
  BETA_FEATURES,
  PROPERTY_DETAILS_TAB_NAMES,
  URL_NAVS,
} from '@/constants';
import * as dashboardActions from '@/dashboard/dashboard-actions';
import * as dataSelectors from '@/data/data-selectors';
import { TaxTabFeatureConfig } from '@/property-tax-reporting-tab/types';
import { useMarkTaxItemAsInvestigated } from '@/top-level-tot/hooks/use-mark-tax-item-as-investigated';
import { useTOTReportingServerSideDatasource } from '@/top-level-tot/hooks/use-tot-reporting-server-side-datasource';
import {
  LicensePayment,
  PaymentPeriodDateRange,
  TopLevelTOTFilters,
} from '@/top-level-tot/types';
import { createDefaultTOTColumns } from '@/top-level-tot/utils/create-columns/create-default-tot-columns';
import * as userSelectors from '@/user/user-selectors';
import { usePropertyDetails } from '@/utils/hooks/data/usePropertyDetails';
import { useTimezone } from '@/utils/hooks/useTimezone';
import { toaster } from '@/utils/toaster';
import { GridOptions, RowClickedEvent } from '@ag-grid-community/core';
import { AgGridReact as AgGridReactType } from '@ag-grid-community/react/lib/agGridReact';
import * as Sentry from '@sentry/browser';
import classNames from 'classnames';
import {
  ServerSidePaginationEnabledAGGridTable,
  ServerSidePaginationEnabledAGGridTableProps,
} from 'deckard-ui/dist/components/AGGridTable/server-side-pagination-table';
import { TranslateFunction } from 'deckard-ui/dist/utils/translator';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  TableContextMenuItemsFeatureFlags,
  contextMenuMaker,
} from '../table-context-menu/context-menu-maker';
import { ContextMenuItemsExtensions } from '../table-context-menu/types';
import {
  agGridColumnTypes,
  agGridComponents,
  agGridSidebar,
  defaultColDef,
  modules,
} from './table-options';

interface Props extends Partial<ServerSidePaginationEnabledAGGridTableProps> {
  filters: TopLevelTOTFilters;
  period: PaymentPeriodDateRange;
  searchQuery: string;
  taxStatus: PaymentStatus | undefined;
  translate: TranslateFunction;
}

export function TopLevelTOTAGGridTable(props: Props) {
  const { period, searchQuery, taxStatus, translate } = props;

  const { goToPropertyDetails } = usePropertyDetails();

  const gridApi = useRef<AgGridReactType>(null);

  const dispatch = useDispatch();

  const place: string = useSelector(userSelectors.currentCity);

  const propertyOverviewTaxTab: TaxTabFeatureConfig = useSelector(
    dataSelectors.betaFeatureConfigValue(BETA_FEATURES.propertyOverviewTaxTab)
  );
  const propertyTaxTabFeatureEnabled = propertyOverviewTaxTab?.enabled === true;

  const timezone = useTimezone();

  const { markTaxItemAsInvestigated } = useMarkTaxItemAsInvestigated({
    gridApi,
  });

  const defaultTOTColumnGroups = useMemo(
    () => createDefaultTOTColumns({ translate, timezone }),
    [timezone, translate]
  );

  const {
    serverSideDatasource: totReportingServerSideDatasource,
    sortChangedHandler,
  } = useTOTReportingServerSideDatasource({
    gridApi,
    period,
    searchQuery,
    taxStatus,
    filters: props.filters,
  });

  const onRowClick: GridOptions['onRowClicked'] = useCallback(
    (event: RowClickedEvent) => {
      const data: Partial<LicensePayment> = event?.node?.data ?? {};
      const propertyDeckardId = data.property?.propertyId;

      if (!propertyDeckardId) {
        return Sentry.captureEvent({
          message: `Failed to open property from Top Level TOT Report table`,
          contexts: {
            context: {
              // Provide property related data
              ...(data.property ?? {}),
            },
          },
        });
      }

      const tabToOpen = propertyTaxTabFeatureEnabled
        ? PROPERTY_DETAILS_TAB_NAMES.propertyOverviewTaxTab
        : PROPERTY_DETAILS_TAB_NAMES.overview;

      goToPropertyDetails({
        propertyDeckardId,
        propertyDetailsTab: tabToOpen,
        tab: URL_NAVS.home,
      });

      if (propertyTaxTabFeatureEnabled) {
        dispatch(
          dashboardActions.updatePermitContextRecordItem({
            field: 'licenseNumber',
            value: data.licenseNumber ?? null,
          })
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [propertyTaxTabFeatureEnabled]
  );

  const handleMarkAsInvestigated = useCallback(
    async (taxItemId: string) => {
      const dialog = window.confirm(
        'Are you sure you want to mark this tax item as investigated?'
      );
      if (dialog) {
        try {
          await markTaxItemAsInvestigated(taxItemId);
        } catch (error) {
          Sentry.captureException(error);
          toaster.error(
            `Error occurred while marking the tax item with id: ${taxItemId} as investigated`
          );
        }
      }
    },
    [markTaxItemAsInvestigated]
  );

  const contextMenuMakerFn = useMemo(() => {
    const featureFlags: TableContextMenuItemsFeatureFlags = {
      propertyTaxTab: propertyTaxTabFeatureEnabled,
    };

    const extensions: ContextMenuItemsExtensions = {
      onMarkAsInvestigated: handleMarkAsInvestigated,
    };

    return contextMenuMaker(place, featureFlags, extensions);
  }, [handleMarkAsInvestigated, place, propertyTaxTabFeatureEnabled]);

  // Effect for assigning a function that sets the row node ID to LicensePayment records after the gridApi ref is created.
  // This is needed for getRowId to work and to manually retrieve the row node.
  useEffect(() => {
    if (gridApi.current) {
      gridApi.current.api.setGetRowNodeId((data) => {
        return data.id;
      });
    }
  }, [gridApi]);

  return (
    <div className="w-full h-[75vh]">
      <div
        id="top-level-tot-grid-container"
        className={classNames(
          'ag-theme-balham',
          'w-full',
          'h-full',
          '[&_.ag-cell-value]:content-center',
          '[&_.ag-column-select-column-drag-handle]:hidden',
          '[&_.ag-column-select-column-group-drag-handle]:hidden'
        )}
      >
        <ServerSidePaginationEnabledAGGridTable
          gridRef={gridApi}
          rowHeight={42}
          serverSideDatasource={totReportingServerSideDatasource}
          onRowClicked={onRowClick}
          onSortChanged={sortChangedHandler}
          defaultColDef={defaultColDef}
          columnDefs={defaultTOTColumnGroups}
          getContextMenuItems={contextMenuMakerFn}
          components={agGridComponents}
          columnTypes={agGridColumnTypes}
          sideBar={agGridSidebar}
          modules={modules}
          alwaysShowHorizontalScroll={true}
          // Prevents the user from re-arranging columns via drag and drop
          suppressMovableColumns
          suppressColumnMoveAnimation
        />
      </div>
    </div>
  );
}
